import { useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { challengeById, type ChallengeDef } from '@il-colpo/meta/progress';
import { useAccount } from '../../account/accountStore.ts';
import { useLooseT, type LooseT } from '../../game/text/loose.ts';
import { useMetaClient, useRemote, type Remote } from '../../meta/hooks.ts';
import type { PlayerProgress } from '../../meta/types.ts';
import { Button, Panel } from '../../ui/index.ts';
import { useAccountSession } from '../account/common.tsx';
import styles from './Meta.module.css';

export type MetaGate = 'unavailable' | 'loading' | 'signIn' | 'ready';

/** Whether there is a signed-in account to read progress from, and if not, what stands in the way. */
export function useMetaGate(): MetaGate {
  const { available, load, snapshot } = useAccountSession();
  if (!available) return 'unavailable';
  if (load !== 'ready' || snapshot.status === 'loading') return 'loading';
  if (snapshot.status !== 'signedIn' || snapshot.needsSecondFactor) return 'signIn';
  return 'ready';
}

/** What a screen shows in place of its content when it is not `ready`. */
export function GatePanel({ gate }: { gate: Exclude<MetaGate, 'ready'> }) {
  const { t } = useTranslation('meta');
  const navigate = useNavigate();
  if (gate === 'loading') {
    return (
      <Panel>
        <p className={styles['note']} role="status">
          {t('gate.loading')}
        </p>
      </Panel>
    );
  }
  if (gate === 'unavailable') {
    return (
      <Panel title={t('gate.unavailable')}>
        <p className={styles['note']}>{t('gate.unavailableBody')}</p>
      </Panel>
    );
  }
  return (
    <Panel title={t('gate.signIn')}>
      <div className={styles['stack']}>
        <p className={styles['note']}>{t('gate.signInBody')}</p>
        <div>
          <Button
            onClick={() => {
              void navigate('/account');
            }}
          >
            {t('gate.signInAction')}
          </Button>
        </div>
      </div>
    </Panel>
  );
}

/** Says a request failed, with a way to try again. */
export function Failed({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('meta');
  return (
    <Panel>
      <div className={styles['stack']}>
        <p className={styles['error']} role="alert">
          {t('gate.failed')}
        </p>
        <div>
          <Button variant="secondary" onClick={onRetry}>
            {t('gate.retry')}
          </Button>
        </div>
      </div>
    </Panel>
  );
}

/** The player's progress, read when the screen opens (null until the account is there). */
export function useProgress(enabled: boolean): Remote<PlayerProgress> {
  const meta = useMetaClient();
  const load = useMemo(() => (meta ? () => meta.progress(Date.now()) : null), [meta]);
  return useRemote<PlayerProgress>(enabled ? load : null);
}

/** The sentence of a challenge, with its goal and, for a role or a map, its name. */
export function challengeTitle(def: ChallengeDef, tm: LooseT, g: LooseT): string {
  const [kind = def.metric, value = ''] = def.metric.split(':');
  const role = kind === 'role' ? g(`roles.${value}`) : '';
  const map = kind === 'map' ? g(`maps.${value}.name`) : '';
  return tm(`challenge.${kind}`, { count: def.goal, role, map });
}

/** The sentence of a challenge by its id (a heist summary names them by id). */
export function useChallengeTitle(): (id: string) => string {
  const tm = useLooseT('meta');
  const g = useLooseT('game');
  return (id) => {
    const def = challengeById(id);
    return def ? challengeTitle(def, tm, g) : id;
  };
}

export function Page({ children }: { children: ReactNode }) {
  return <div className={styles['page']}>{children}</div>;
}

/** Whether the build can show progress at all (an account service is there to read it from). */
export function useMetaAvailable(): boolean {
  return useAccount((s) => s.setup.available);
}
